import { Link } from "react-router-dom";
import LegalPage, { LList, EmailLink } from "@/components/LegalPage";

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body: (
      <p>
        By accessing or using Abhyas, you agree to these Terms of Service. If
        you do not agree with any part of these Terms, please do not use the
        platform.
      </p>
    ),
  },
  {
    title: "2. About Abhyas",
    body: (
      <>
        <p>
          Abhyas is an educational technology platform that helps students
          practice through AI-generated questions, explanations, solved
          answers, progress tracking, and related learning features.
        </p>
        <p>
          Abhyas is intended as a supplementary practice tool. It does not
          replace schools, teachers, textbooks, official curriculum materials,
          or professional educational guidance.
        </p>
      </>
    ),
  },
  {
    title: "3. Eligibility and Student Use",
    body: (
      <p>
        You must provide accurate information when creating an account and
        should have the necessary permission or consent where required to use
        the service (including, where applicable, a parent or guardian's
        involvement for students).
      </p>
    ),
  },
  {
    title: "4. User Accounts",
    body: (
      <>
        <p>As an account holder, you are responsible for:</p>
        <LList
          items={[
            "Keeping your login credentials secure",
            "Providing accurate information",
            "Not sharing your account where prohibited",
            "All activity performed through your account",
            "Reporting any unauthorized access to us promptly",
          ]}
        />
      </>
    ),
  },
  {
    title: "5. Educational Content",
    body: (
      <>
        <p>
          Abhyas may generate questions, explanations, answers, and other
          educational material using AI. AI-generated content may contain
          mistakes, inaccuracies, outdated information, or incomplete
          explanations.
        </p>
        <p>
          Students should verify important information using official
          textbooks, teachers, school materials, or other reliable sources
          before relying on it.
        </p>
      </>
    ),
  },
  {
    title: "6. Acceptable Use",
    body: (
      <>
        <p>You must not:</p>
        <LList
          items={[
            "Attempt to hack or disrupt the platform",
            "Circumvent subscription limits or paywalls",
            "Abuse API or AI-generation systems",
            "Use automated systems to generate excessive requests",
            "Reverse engineer or copy the platform",
            "Upload unlawful, harmful, or malicious content",
            "Attempt unauthorized access to another user's account",
            "Use Abhyas for fraudulent or illegal activities",
          ]}
        />
      </>
    ),
  },
  {
    title: "7. Subscriptions and Plans",
    body: (
      <>
        <p>
          Abhyas offers subscription plans — currently Starter, Pro, and Elite
          plans. Pricing, included features, and
          usage limits are displayed on the Pricing section before purchase.
        </p>
        <p>
          Subscriptions may automatically renew where applicable. You are
          responsible for cancelling before the next billing cycle if you do
          not wish to continue.
        </p>
      </>
    ),
  },
  {
    title: "8. Payments",
    body: (
      <p>
        Payments may be processed through third-party payment providers such as
        Razorpay or another authorized payment processor. Payment processing is
        subject to the applicable provider's own terms and policies.
      </p>
    ),
  },
  {
    title: "9. Cancellation and Refunds",
    body: (
      <p>
        Cancellation and refund requests are governed by our separate{" "}
        <Link
          to="/refund-cancellation"
          data-testid="terms-refund-link"
          className="font-bold text-[#16A34A] hover:underline"
        >
          Refund &amp; Cancellation Policy
        </Link>
        .
      </p>
    ),
  },
  {
    title: "10. Intellectual Property",
    body: (
      <>
        <p>
          Abhyas's branding, logo, website design, software, original content,
          and platform functionality belong to Abhyas or its licensors and may
          not be copied or commercially exploited without permission.
        </p>
        <p>
          You retain appropriate rights over content you independently provide,
          subject to the licenses necessary for us to operate the service.
        </p>
      </>
    ),
  },
  {
    title: "11. Third-Party Services",
    body: (
      <p>
        Abhyas may depend on third-party infrastructure, AI services, analytics
        services, payment processors, hosting providers, and other
        integrations. We are not responsible for the availability or conduct of
        third-party services.
      </p>
    ),
  },
  {
    title: "12. Availability and Service Changes",
    body: (
      <p>
        Abhyas may modify, suspend, or discontinue features when reasonably
        necessary for maintenance, security, development, or business reasons.
        We do not guarantee uninterrupted availability of the platform.
      </p>
    ),
  },
  {
    title: "13. Disclaimer",
    body: (
      <p>
        Abhyas is an educational practice tool. We do not guarantee exam
        results, grades, academic performance, admission, or any particular
        educational outcome. AI-generated answers may contain errors, and your
        use of the platform is at your own discretion.
      </p>
    ),
  },
  {
    title: "14. Limitation of Liability",
    body: (
      <p>
        To the extent permitted by applicable law, Abhyas and its team shall
        not be liable for indirect, incidental, or consequential damages
        arising from your use of the platform, including reliance on
        AI-generated content. Where liability cannot be excluded, it shall be
        limited to the amount you paid for the service in the relevant period,
        to the extent permitted by law.
      </p>
    ),
  },
  {
    title: "15. Termination",
    body: (
      <p>
        Abhyas may suspend or terminate accounts in circumstances including
        serious violations of these Terms, fraud, abuse, security threats, or
        unlawful activity. You may stop using the service at any time.
      </p>
    ),
  },
  {
    title: "16. Changes to Terms",
    body: (
      <p>
        We may update these Terms from time to time. The updated version will
        be posted on this page, and continued use of Abhyas after changes are
        posted means you accept the updated Terms.
      </p>
    ),
  },
  {
    title: "17. Governing Law",
    body: (
      <p>
        These Terms are intended to be governed by the applicable laws of
        India, and disputes will be subject to the jurisdiction of the
        appropriate courts, with the exact jurisdiction to be finalized by
        Abhyas's legal advisor.
      </p>
    ),
  },
  {
    title: "18. Contact",
    body: (
      <p>
        For questions about these Terms, contact us at <EmailLink />.
      </p>
    ),
  },
];

const TermsOfService = () => (
  <LegalPage
    testid="terms-of-service-page"
    title="Terms of Service"
    intro="These Terms govern your use of Abhyas. Please read them carefully before using the platform."
    sections={SECTIONS}
  />
);

export default TermsOfService;
